import { useEffect, useRef } from "react";

import { useConversationMessages } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { ChatMessage } from "~/ai-chat/model/chat-message";
import type { ConversationMessage } from "~/ai-chat/model/conversation";

function toTranscriptItem(message: ConversationMessage): ChatMessage {
  return { kind: "message", id: message.id, role: message.role, content: message.content, createdAt: message.created };
}

// Loads a selected conversation's persisted messages into the transcript once per
// switch. Guarded so it hydrates only when switching TO a conversation (not during
// its own live turn — the unsynced flag protects the in-flight reply), and once a
// conversation is hydrated this session its local transcript wins until the user
// switches away and back. Mount this once at a level that outlives the history
// list (the page / widget shell) so switching still hydrates with the list closed
// — e.g. from the chat tabs.
export function useHydrateActiveConversation() {
  const { activeConversationId, replaceMessages, isConversationUnsynced } = useAiChat();
  const messagesQuery = useConversationMessages(activeConversationId);
  const hydratedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeConversationId === null) {
      hydratedIdRef.current = null;
      return;
    }
    // The id also changes when the first send persists a live chat — its turn hasn't
    // finished appending server-side yet, so hydrating would wipe the in-flight reply.
    if (isConversationUnsynced(activeConversationId)) {
      hydratedIdRef.current = activeConversationId;
      return;
    }
    if (activeConversationId !== hydratedIdRef.current && messagesQuery.data !== undefined) {
      replaceMessages(messagesQuery.data.map(toTranscriptItem));
      hydratedIdRef.current = activeConversationId;
    }
  }, [activeConversationId, messagesQuery.data, replaceMessages, isConversationUnsynced]);
}
